'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import {
  Collapsible as CollapsibleUi,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@packages/shared/src/ui/Collapsible/Collapsible';
import { InstallmentValueTilesContainer } from '../../../atoms/InstallmentValueTilesContainer/InstallmentValueTilesContainer.client';
import { PaymentPause } from '../../../atoms/PaymentPause/PaymentPause.client';
import { LegalHints } from '../../../atoms/LegalHints/LegalHints.client';
import { InstallmentPriceBox } from '../../../atoms/InstallmentPriceBox/InstallmentPriceBox.client';
import { PaymentPlan } from '../../../atoms/PaymentPlan/PaymentPlan.client';
import type { InstallmentCalulatorLayoutComponentsDefaultProps } from '../types';
import { Slider } from '../../../atoms/Slider/Slider.client';
import { InfoCallToAction } from '../../../atoms/InfoCallToAction/InfoCallToAction.client';
import { LegalHintsImage } from '../../../atoms/LegalHintsImage/LegalHintsImage.client';

export type CollapsibleProps = InstallmentCalulatorLayoutComponentsDefaultProps & {
  /**
   * should the payment plan section be collapsed/closed
   */
  paymentPlanCollapsed?: boolean;
};

/**
 * Calculator component
 *
 * component to display the fixed installment calculator
 *
 * */
export const Collapsible = ({
  calculationResult,
  calculationValuta,
  isLoading,
  paymentPlanCollapsed = true,
  hasEditablePrice,
  onChangeInstallmentValue,
  onChangePaymentPauseValue,
  onChangeBasePriceValue,
}: CollapsibleProps) => {
  const {
    currency,
    singleInstallment,
    viableInstallments,
    numberOfInstallments,
    paymentPause,
    valuta,
    supportsPaymentPause,
    type,
    minValue,
    maxValue,
  } = calculationResult;
  const {
    installmentCalculator: { hasLegalHints, legalHintsImage, hasInfoCallToAction },
  } = useConfig();
  const { formatMessage } = useIntl();

  const [isOpen, setIsOpen] = useState(!paymentPlanCollapsed);

  useEffect(() => {
    setIsOpen(!paymentPlanCollapsed);
  }, [paymentPlanCollapsed]);

  const handleCollapsed = (openProp: boolean) => {
    setIsOpen(openProp);
  };

  return (
    <div className="mt-0 mb-4 flex flex-col flex-wrap gap-1.5 p-2">
      <InstallmentPriceBox
        currency={currency}
        calculationValuta={calculationValuta}
        singleInstallment={singleInstallment}
        isLoading={isLoading}
        hasEditablePrice={hasEditablePrice}
        onChangeBasePriceValue={onChangeBasePriceValue}
        minValue={minValue}
        maxValue={maxValue}
      />
      {hasInfoCallToAction && <InfoCallToAction />}
      {type === 'FIX' && viableInstallments ? (
        <InstallmentValueTilesContainer
          installmentsList={viableInstallments}
          activeInstallment={numberOfInstallments}
          installmentSelectedHandler={onChangeInstallmentValue}
        />
      ) : (
        <Slider
          viableInstallments={viableInstallments}
          numberOfInstallments={numberOfInstallments}
          installmentSelectedHandler={onChangeInstallmentValue}
        />
      )}
      <CollapsibleUi open={isOpen} onOpenChange={handleCollapsed}>
        <CollapsibleContent>
          {supportsPaymentPause && (
            <>
              <Divider className="mt-3 mb-1.5" />
              <div className="mb-1">
                <PaymentPause checked={paymentPause} checkHandler={onChangePaymentPauseValue} />
              </div>
            </>
          )}
          <PaymentPlan asBox={false} calculationResult={calculationResult} />
          {hasLegalHints && (
            <div className="mt-3">
              <LegalHints paymentPauseValuta={valuta} />
            </div>
          )}
        </CollapsibleContent>
        <Divider className="-mx-2" />
        <CollapsibleTrigger className="flex cursor-pointer flex-row flex-nowrap items-center py-2">
          <span className="text-size-body2">
            {formatMessage(
              isOpen
                ? {
                    id: 'ic-collapsible-minimize',
                    defaultMessage: 'Ratenrechner minimieren',
                  }
                : {
                    id: 'ic-collapsible-maximize',
                    defaultMessage: 'Ratenrechner anzeigen',
                  },
            )}
          </span>
          <span
            className={`icon-emp-arrow-medium-down bg-color-grey-dark h-4 w-4 ${isOpen ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
      </CollapsibleUi>
      {legalHintsImage !== null && <LegalHintsImage />}
    </div>
  );
};
